import { Button, Col, Icon, Input, openUrl, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useState } from "react";
import { say } from "../../localization/say.ts";
import { act } from "../../net/rally.ts";
import type { EditableApp } from "./app-controls.tsx";

import { AppShippingControls } from "./app-shipping-controls.tsx";

type Editing = "source" | "credential" | null;

export function AppSourceControls({
  app,
  reload,
}: {
  app: Pick<EditableApp, "id" | "placements" | "repositories" | "shipping" | "setupAvailable">;
  reload: () => void;
}) {
  const theme = useTheme();
  const project = app.placements[0]?.context === "project" ? app.placements[0] : undefined;
  const projectShipping = project?.contextId
    ? app.shipping.filter((rule) => rule.project === project.contextId)
    : [];
  const missingCredential = app.repositories.find((repository) => !repository.token.held) ?? null;
  const credentialReady = app.repositories.length > 0 && !missingCredential;
  const shippedRepositories = new Set(projectShipping.map((rule) => rule.repo));
  const shippingReady =
    app.repositories.length > 0 &&
    app.repositories.every((repository) => shippedRepositories.has(repository.name));
  const ready = credentialReady && shippingReady;
  const [editing, setEditing] = useState<Editing>(null);
  const [repoName, setRepoName] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [repoToken, setRepoToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const field = {
    minHeight: 44,
    borderWidth: 1,
    borderColor: theme.color["border.default"],
    borderRadius: theme.radius.md,
    paddingHorizontal: theme.space[3],
    color: theme.color["text.primary"],
  };
  const saved = () => {
    setEditing(null);
    setRepoToken("");
    reload();
  };
  const failed = () => setFailure(say("apps.saveFailed"));
  const run = async (name: string, body: object) => {
    setSaving(true);
    setFailure("");
    try {
      await act(name, body);
      saved();
    } catch {
      failed();
    } finally {
      setSaving(false);
    }
  };
  const completeSetup = async (project: string) => {
    setSaving(true);
    setFailure("");
    try {
      await act("app.completeSetup", { app: app.id, project });
      saved();
    } catch {
      failed();
    } finally {
      setSaving(false);
    }
  };
  const sourceValid =
    /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(repoName.trim()) &&
    (() => {
      try {
        return new URL(repoUrl.trim()).protocol === "https:";
      } catch {
        return false;
      }
    })();
  const tokenValid = repoToken.length >= 8 && !/\s/.test(repoToken);
  return (
    <Col style={{ gap: theme.space[2] }} testID="app-setup">
      <Col
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space[2],
          paddingHorizontal: theme.space[2],
          paddingVertical: theme.space[2],
        }}
      >
        <Icon
          name={ready ? "circle-check" : "circle-alert"}
          size={17}
          color={ready ? "accent" : "text.tertiary"}
        />
        <Col style={{ flex: 1 }}>
          <Text typeName="bodySemibold" color="text.primary">
            {ready ? say("apps.setupReady") : say("apps.setupNeeded")}
          </Text>
          <Text typeName="caption" color="text.tertiary">
            {ready ? say("apps.setupReadyDetail") : say("apps.setupNeededDetail")}
          </Text>
        </Col>
        {!ready && project && app.setupAvailable ? (
          <Button
            size="sm"
            disabled={saving}
            testID="app-complete-setup"
            onPress={() => project.contextId && void completeSetup(project.contextId)}
          >
            {saving ? say("apps.settingUp") : say("apps.finishSetup")}
          </Button>
        ) : null}
      </Col>

      <StatusRow
        icon="box"
        label={say("apps.project")}
        value={project?.contextName ?? say("apps.notPlaced")}
      />

      {app.repositories.length ? (
        app.repositories.map((repository) => (
          <Tappable
            key={repository.name}
            accessibilityRole="link"
            accessibilityLabel={`${say("apps.repository")}: ${repository.name}`}
            onPress={() => void openUrl(repository.url)}
            style={{
              minHeight: 44,
              flexDirection: "row",
              alignItems: "center",
              gap: theme.space[2],
              paddingHorizontal: theme.space[2],
              paddingVertical: theme.space[2],
              borderRadius: theme.radius.md,
            }}
          >
            <Icon name="git-branch" size={17} color="text.tertiary" />
            <Col style={{ flex: 1, minWidth: 0 }}>
              <Text typeName="body" color="text.primary" numberOfLines={1}>
                {say("apps.repository")}
              </Text>
              <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
                {repository.name}
              </Text>
            </Col>
            <Icon name="external-link" size={15} color="text.tertiary" />
          </Tappable>
        ))
      ) : (
        <Tappable
          accessibilityRole="button"
          testID="app-add-source"
          onPress={() => setEditing(editing === "source" ? null : "source")}
          style={{
            minHeight: 44,
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[2],
            paddingHorizontal: theme.space[2],
            borderRadius: theme.radius.md,
          }}
        >
          <Icon name="git-branch" size={17} color="text.tertiary" />
          <Text typeName="body" color="text.primary" style={{ flex: 1 }}>
            {say("apps.repository")}
          </Text>
          <Text typeName="body" color="text.tertiary">
            {say("apps.addRepository")}
          </Text>
          <Icon name="chevron-right" size={15} color="text.tertiary" />
        </Tappable>
      )}

      {editing === "source" ? (
        <Col style={{ gap: theme.space[2], paddingHorizontal: theme.space[2] }} testID="app-source-editor">
          <Text typeName="label" color="text.secondary">
            {say("apps.sourceName")}
          </Text>
          <Input
            value={repoName}
            onChangeText={setRepoName}
            accessibilityLabel={say("apps.sourceName")}
            style={field}
          />
          <Text typeName="label" color="text.secondary">
            {say("apps.sourceUrl")}
          </Text>
          <Input
            value={repoUrl}
            onChangeText={setRepoUrl}
            accessibilityLabel={say("apps.sourceUrl")}
            autoCapitalize="none"
            style={field}
          />
          <Col style={{ flexDirection: "row", justifyContent: "flex-end", gap: theme.space[2] }}>
            <Tappable
              accessibilityRole="button"
              onPress={() => setEditing(null)}
              style={{ padding: theme.space[2] }}
            >
              <Text typeName="label" color="text.secondary">
                {say("apps.cancel")}
              </Text>
            </Tappable>
            <Button
              disabled={!sourceValid || saving}
              onPress={() =>
                void run("app.attachRepository", { app: app.id, name: repoName.trim(), url: repoUrl.trim() })
              }
            >
              {say("apps.saveSource")}
            </Button>
          </Col>
        </Col>
      ) : null}

      <Tappable
        accessibilityRole="button"
        accessibilityState={{ disabled: !missingCredential }}
        disabled={!missingCredential}
        testID="app-credential-row"
        onPress={() => setEditing(editing === "credential" ? null : "credential")}
        style={{
          minHeight: 44,
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space[2],
          paddingHorizontal: theme.space[2],
          borderRadius: theme.radius.md,
        }}
      >
        <Icon name="key" size={17} color="text.tertiary" />
        <Text typeName="body" color="text.primary" style={{ flex: 1 }}>
          {say("apps.repositoryAccess")}
        </Text>
        <Text typeName="body" color={credentialReady ? "accent" : "text.tertiary"}>
          {credentialReady
            ? say("apps.connected")
            : app.repositories.length === 0
              ? say("apps.repositoryNeeded")
              : say("apps.accessNeeded")}
        </Text>
        {missingCredential ? <Icon name="chevron-right" size={15} color="text.tertiary" /> : null}
      </Tappable>

      {editing === "credential" && missingCredential ? (
        <Col
          style={{ gap: theme.space[2], paddingHorizontal: theme.space[2] }}
          testID="app-credential-editor"
        >
          <Text typeName="label" color="text.secondary">
            {say("apps.sourceToken")}
          </Text>
          <Input
            value={repoToken}
            onChangeText={setRepoToken}
            secureTextEntry
            autoCapitalize="none"
            accessibilityLabel={say("apps.sourceToken")}
            style={field}
          />
          <Col style={{ flexDirection: "row", justifyContent: "flex-end", gap: theme.space[2] }}>
            <Tappable
              accessibilityRole="button"
              onPress={() => setEditing(null)}
              style={{ padding: theme.space[2] }}
            >
              <Text typeName="label" color="text.secondary">
                {say("apps.cancel")}
              </Text>
            </Tappable>
            <Button
              disabled={!tokenValid || saving}
              onPress={() =>
                void run("app.setRepoToken", {
                  app: app.id,
                  repo: missingCredential.name,
                  token: repoToken,
                })
              }
            >
              {say("apps.connectAccess")}
            </Button>
          </Col>
        </Col>
      ) : null}

      <AppShippingControls app={app} reload={reload} />

      {failure ? (
        <Text typeName="caption" color="danger" style={{ paddingHorizontal: theme.space[2] }}>
          {failure}
        </Text>
      ) : null}
    </Col>
  );
}

function StatusRow({
  icon,
  label,
  value,
  ready,
}: {
  icon: "box" | "rocket";
  label: string;
  value: string;
  ready?: boolean;
}) {
  const theme = useTheme();
  return (
    <Col
      style={{
        minHeight: 44,
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[2],
        paddingHorizontal: theme.space[2],
      }}
    >
      <Icon name={icon} size={17} color="text.tertiary" />
      <Text typeName="body" color="text.primary" style={{ flex: 1 }}>
        {label}
      </Text>
      <Text typeName="body" color={ready ? "accent" : "text.tertiary"} numberOfLines={1}>
        {value}
      </Text>
    </Col>
  );
}
